<script>
import TranscriptSummaryCard from '@modules/crm/components/transcript-summary-card/transcript-summary-card.vue'
import TranscriptSentimentGauge from '@modules/crm/components/transcript-sentiment-gauge/transcript-sentiment-gauge.vue'
import TranscriptStateFeedback from '@modules/crm/components/transcript-state-feedback/transcript-state-feedback.vue'

// Hex equivalentes aos tokens de sentimento (a barra de confiança precisa de hex).
const SENTIMENT_HEX = {
  positivo: '#56AF79',
  neutro: '#E0AF00',
  negativo: '#EB5757',
}

export default {
  name: 'DetailTabSentimento',
  components: {
    TranscriptSummaryCard,
    TranscriptSentimentGauge,
    TranscriptStateFeedback,
  },
  props: {
    transcript: { type: Object, default: null },
  },
  computed: {
    sentimento() {
      return (this.transcript && this.transcript.sentimento_atual) || null
    },
    sentimentoScore() {
      return (this.transcript && this.transcript.sentimento_score) || null
    },
    engajamento() {
      return (this.transcript && this.transcript.engajamento_nivel) || null
    },
    sentimentoHex() {
      return SENTIMENT_HEX[this.sentimento] || '#9E9E9E'
    },
    // Confiança = score bruto (0-1) + rótulo qualitativo derivado.
    confiancaValor() {
      if (this.sentimentoScore === null || this.sentimentoScore === undefined) {
        return null
      }
      const num = Number(this.sentimentoScore)
      return Number.isNaN(num) ? null : num
    },
    confiancaLabel() {
      const v = this.confiancaValor
      if (v === null) return ''
      if (v < 0.4) return 'Baixo'
      if (v < 0.7) return 'Médio'
      return 'Alto'
    },
    sentimentoJustificativa() {
      return (this.transcript && this.transcript.sentimento_justificativa) || ''
    },
    // Sem sentimento: distingue "aguardando IA" de "vazio" e já retorna as props
    // do TranscriptStateFeedback (state + ícone + mensagem).
    sentimentoFeedback() {
      const aiStatus = (this.transcript && this.transcript.ai_status) || ''
      const processing = ['queued', 'processing', 'pending'].includes(aiStatus)
      return {
        state: processing ? 'processing' : 'empty',
        icon: 'mdi-emoticon-neutral',
        message: processing
          ? 'Analisando o sentimento da reunião…'
          : 'Sentimento ainda não disponível para esta reunião.',
      }
    },
  },
}
</script>

<template>
  <div>
    <!-- Layout em flex (sem v-row p/ evitar scroll horizontal das margens
         negativas) com align-items: stretch → os dois cards na mesma altura. -->
    <div
      v-if="sentimento"
      class="d-flex flex-column flex-md-row az-gap-small detail-tab-sentimento__cards"
    >
      <!-- Score de Sentimento (gauge donut + nome + engajamento) -->
      <TranscriptSummaryCard
        title="Score de Sentimento"
        class="detail-tab-sentimento__card detail-tab-sentimento__card--left"
      >
        <div class="d-flex flex-column align-center">
          <TranscriptSentimentGauge
            :sentimento="sentimento"
            :score="sentimentoScore"
            :engajamento="engajamento"
            :size="120"
            show-scale
          />

          <div v-if="confiancaValor !== null" class="w-100 mt-4">
            <div class="d-flex align-center justify-space-between mb-1">
              <span class="az-text-small">
                Confiança ({{ confiancaValor.toFixed(2) }})
              </span>
              <span class="az-text-small">{{ confiancaLabel }}</span>
            </div>
            <v-progress-linear
              :value="confiancaValor * 100"
              :color="sentimentoHex"
              height="8"
              rounded
            />
          </div>
        </div>
      </TranscriptSummaryCard>

      <!-- Análise da IA -->
      <TranscriptSummaryCard
        title="Análise da IA"
        icon="ia-head"
        class="detail-tab-sentimento__card detail-tab-sentimento__card--right"
      >
        <div
          v-if="sentimentoJustificativa"
          class="detail-tab-sentimento__analysis"
        >
          <p class="az-text-primary az-size-14 mb-0">
            {{ sentimentoJustificativa }}
          </p>
        </div>
        <p v-else class="az-text-secondary az-size-14 mb-0">
          Análise da IA ainda não disponível.
        </p>
      </TranscriptSummaryCard>
    </div>

    <TranscriptStateFeedback v-else v-bind="sentimentoFeedback" />
  </div>
</template>

<style lang="sass" scoped>
.detail-tab-sentimento
  // Cards lado a lado com a mesma altura (align stretch do flex). mb-0 sobrescreve
  // o mb-3 do card pra não desalinhar a base no modo lado a lado.
  &__cards
    align-items: stretch

  &__card
    min-width: 0
    margin-bottom: 0 !important

  &__card--left
    flex: 5 1 0

  &__card--right
    flex: 7 1 0

  &__analysis
    background: #F5F8FF
    border-radius: 8px
    padding: 14px 16px
</style>
